import React, { useState, useMemo } from 'react';
import { Card, Modal, Input, Row, Col, Typography, Spin, Empty } from 'antd';
import { SearchOutlined, UserOutlined } from '@ant-design/icons';
import CardImage from '../CardImage';
import './LeaderPicker.css';

const { Text } = Typography;

/**
 * Image-based leader picker. Uses Ant Design components.
 * Props: options (array of { card_id, name, img_full_url }), value (card_id), onChange(card_id), loading?, placeholder?, label?
 */
function normalizeValue(value) {
  if (value == null) return null;
  if (typeof value === 'object' && 'card_id' in value) return value.card_id;
  if (typeof value === 'string' || typeof value === 'number') return value;
  return null;
}

export default function LeaderPicker({ options = [], value, onChange, loading = false, placeholder = 'Choose leader', label = 'Leader' }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const safeOptions = Array.isArray(options) ? options.filter((c) => c && (c.card_id != null || c.name != null)) : [];
  const cardId = normalizeValue(value);

  const selectedCard = useMemo(
    () => safeOptions.find((c) => String(c.card_id) === String(cardId)),
    [safeOptions, cardId]
  );

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return safeOptions;
    const q = String(search).trim().toLowerCase();
    return safeOptions.filter(
      (c) =>
        (c.name && String(c.name).toLowerCase().includes(q)) ||
        (c.card_id && String(c.card_id).toLowerCase().includes(q))
    );
  }, [safeOptions, search]);

  const handleSelect = (card) => {
    const id = card && (card.card_id != null) ? card.card_id : null;
    if (typeof onChange === 'function') onChange(id);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="leader-picker">
      {label && <Text type="secondary" className="leader-picker-label">{label}</Text>}
      <Card
        size="small"
        className={`leader-picker-trigger ${selectedCard ? 'leader-picker-trigger-selected' : ''}`}
        hoverable
        onClick={() => !loading && setOpen(true)}
      >
        {loading ? (
          <div className="leader-picker-trigger-loading">
            <Spin size="small" />
            <Text type="secondary">Loading…</Text>
          </div>
        ) : selectedCard ? (
          <div className="leader-picker-trigger-content">
            <div className="leader-picker-trigger-img-wrap">
              {selectedCard.img_full_url ? (
                <CardImage
                  src={selectedCard.img_full_url}
                  alt={selectedCard.name}
                  wrapperClassName="leader-picker-trigger-img-inner"
                />
              ) : (
                <div className="leader-picker-trigger-placeholder"><UserOutlined /></div>
              )}
            </div>
            <div className="leader-picker-trigger-info">
              <Text strong>{selectedCard.name}</Text>
              <Text type="secondary" className="leader-picker-trigger-id">{selectedCard.card_id}</Text>
            </div>
          </div>
        ) : (
          <div className="leader-picker-trigger-empty">
            <UserOutlined style={{ fontSize: 24, color: '#bfbfbf' }} />
            <Text type="secondary">{placeholder}</Text>
          </div>
        )}
      </Card>

      <Modal
        title={placeholder}
        open={open}
        onCancel={() => { setOpen(false); setSearch(''); }}
        footer={null}
        width={640}
        className="leader-picker-modal"
        destroyOnClose
      >
        <Input
          placeholder="Search by name or card ID"
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ marginBottom: 16 }}
        />
        <div className="leader-picker-grid-wrap">
          {filteredOptions.length === 0 ? (
            <Empty description="No leaders match" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Row gutter={[12, 12]}>
              {filteredOptions.map((card, index) => (
                <Col xs={12} sm={8} md={6} key={card.card_id != null ? card.card_id : `opt-${index}`}>
                  <Card
                    size="small"
                    hoverable
                    className={`leader-picker-option ${cardId != null && String(card.card_id) === String(cardId) ? 'leader-picker-option-selected' : ''}`}
                    onClick={() => handleSelect(card)}
                  >
                    <div className="leader-picker-option-img-wrap">
                      {card.img_full_url ? (
                        <CardImage
                          src={card.img_full_url}
                          alt={card.name || ''}
                          wrapperClassName="leader-picker-option-img-inner"
                          className="leader-picker-option-img"
                        />
                      ) : (
                        <div className="leader-picker-option-placeholder"><UserOutlined /></div>
                      )}
                    </div>
                    <Text className="leader-picker-option-name" ellipsis title={card.name || ''}>
                      {card.name || '—'}
                    </Text>
                    <Text type="secondary" className="leader-picker-option-id">{card.card_id ?? '—'}</Text>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </div>
      </Modal>
    </div>
  );
}
